/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react';
import { Activity, Coins, TrendingUp, Trophy, Users } from 'lucide-react';
import { api, type AdoptionStats, type UserSpendRow } from '../lib/api';
import { ErrorState, Grid, Kpi, PageHeader, Panel, ShareBar, Stack, Table, EmptyState, type Column } from '../ui';
import { count, date, money, ratio, tokens } from '../ui/format';

/**
 * Operations › Usage & adoption › Adoption, rebuilt on the kit (overhaul step 4): who is using Gyro and how much; active
 * people, engagement, and the heaviest users by consumption. People are identified by email (not hashed). The same
 * call as before; money and tokens in the console's one format.
 */
export function Adoption() {
  const [data, setData] = useState<AdoptionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .adoption(10)
      .then(setData)
      .catch((e: unknown) => setError(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const maxTok = data ? Math.max(1, ...data.topUsers.map((u) => u.monthTokens)) : 1;
  const columns: Array<Column<UserSpendRow & { rank: number }>> = [
    { key: 'rank', header: '#', numeric: true, muted: true, width: '48px', cell: (u) => u.rank },
    { key: 'user', header: 'User', mono: true, cell: (u) => u.email },
    { key: 'usage', header: '30-day usage', width: '200px', hideOnPhone: true, cell: (u) => <ShareBar value={ratio(u.monthTokens, maxTok)} label={`${u.email}: share of the heaviest user's tokens`} digits={0} /> },
    { key: 'tokens', header: 'Tokens (30d)', numeric: true, cell: (u) => tokens(u.monthTokens) },
    { key: 'cost', header: 'Cost (30d)', numeric: true, cell: (u) => money(u.monthUsd) },
    { key: 'today', header: 'Today', numeric: true, muted: true, hideOnPhone: true, cell: (u) => (u.dayTokens > 0 ? `${tokens(u.dayTokens)} · ${money(u.dayUsd)}` : '—') },
  ];

  return (
    <>
      <PageHeader meta={data ? <span>Tracking since {date(data.since)}</span> : null} />
      <div className="ui-page">
        {error ? (
          <ErrorState title="Adoption could not be loaded" error={error} onRetry={load} retrying={loading} />
        ) : (
          <Stack>
            <Grid cols={4} label="Active people">
              <Kpi icon={Users} label="Active today" loading={loading} value={data ? count(data.activeToday) : null} sub="DAU" />
              <Kpi icon={Users} label="Active this week" loading={loading} value={data ? count(data.activeWeek) : null} sub="WAU · last 7 days" />
              <Kpi icon={Users} label="Active this month" loading={loading} value={data ? count(data.activeMonth) : null} sub="MAU · last 30 days" />
              <Kpi icon={TrendingUp} label="Stickiness" loading={loading} value={data ? `${data.stickiness}%` : null} sub="DAU ÷ MAU" />
            </Grid>
            <Grid cols={4} label="Volume and cost">
              <Kpi icon={Activity} label="Total interactions" loading={loading} value={data ? count(data.requestsTotal) : null} sub="all time" />
              <Kpi icon={Coins} label="Tokens" loading={loading} value={data ? tokens(data.tokensMonth) : null} sub="last 30 days" />
              <Kpi icon={Coins} label="Cost, last 30 days" loading={loading} value={data ? money(data.costMonth) : null} sub="Anthropic list pricing" />
              <Kpi icon={Coins} label="Cost, all time" loading={loading} value={data ? money(data.costTotal) : null} sub="Anthropic list pricing" />
            </Grid>
            <Panel
              flush
              title={<span className="ui-title-icon"><Trophy size={16} strokeWidth={1.75} aria-hidden="true" />Top 10 users by usage</span>}
              description="Last 30 days, ranked by cost"
              footer={<span>Set per-user limits under Operations › Caps &amp; limits.</span>}
            >
              <Table
                label="Top users by usage"
                columns={columns}
                rows={(data?.topUsers ?? []).map((u, i) => ({ ...u, rank: i + 1 }))}
                rowKey={(u) => u.email}
                loading={loading}
                skeletonRows={5}
                empty={<EmptyState compact icon={Trophy} title="No usage recorded yet">As people use Gyro, the heaviest users appear here.</EmptyState>}
                phone={{ title: (u) => u.email, meta: (u) => `#${u.rank} · ${tokens(u.monthTokens)} tokens`, value: (u) => money(u.monthUsd), sub: () => '30 days' }}
              />
            </Panel>
          </Stack>
        )}
      </div>
    </>
  );
}
